
import Vue from 'vue';
import { Component, Prop, Watch } from 'vue-property-decorator';
import { namespace } from 'vuex-class';

import { PackagesSelectionModal } from '@/lazy-load-components';

import HiddenHorizontalScrollbar from '@/components/HiddenHorizontalScrollbar.vue';
import ProviderIcon from '@/components/picture/ProviderIcon.vue';

import { CollectionType } from '@/enums/collection-type';
import { ProviderFilterPlace } from '@/enums/provider-filter-place';
import type { WebLocale } from '@/enums/web-locale';

import { ModalHelper } from '@/helpers/modal-helper';
import { calculateUrl } from '@/helpers/state-helper';

import Genre from '@/interfaces/genre';
import Provider from '@/interfaces/provider';

import { NewPageType } from '@/@types/graphql-types';
import { providerPageExpTrackEvent } from '@/helpers/experiments/popular-provider-page-exp-helper';
import { getActiveFilters } from '@/helpers/filter-helper';
import { FilterCollection } from '@/stores/filter.store';
import { UserSettings } from '@/stores/user.store';

const Constant = namespace('constant');
const Filter = namespace('filter');
const Routing = namespace('routing');
const User = namespace('user');

@Component({
	name: 'FilterBarProviderSeoFriendly',
	components: { HiddenHorizontalScrollbar, ProviderIcon },
	serverCacheKey: props => {
		const { currentFilters, webLocale, userSettings, collectionType, newPageType } = props;
		const activeFilters = getActiveFilters(Object.keys(currentFilters) as any, currentFilters);
		const { providers = [], ...otherActiveFiltersThanProviders } = activeFilters;
		if (
			webLocale &&
			// e.g. Home.Global has a <FilterBarProvider> usage without collectionType
			collectionType &&
			// no caching if there are other providers active other than providers.
			// we'd need to use every active filter in the caching key, which is too wild (filters end up in the <a href>).
			Object.keys(otherActiveFiltersThanProviders).length === 0 &&
			// either single provider active or all providers active
			providers.length <= 1 &&
			// if single provider is active, that providers needs to be a top X provider
			(providers.length === 0 ||
				props.userProviders.findIndex((p: Provider) => providers[0] === p.short_name) <
					global.NEW_TIMELINE_TITLES_PER_BUCKET_LIMIT) &&
			// no providers set in the userSettings
			(userSettings.providers || []).length === 0
		) {
			const lastFilteredProvider = providers.slice(-1);
			// adding the amount of providers into the key in case of release of new providers doesn't cause hydration issues
			const key = lastFilteredProvider.length === 1 ? lastFilteredProvider[0] : props.userProviders.length;
			return `${webLocale}-${collectionType}-${newPageType}-${key}`;
		}
		return false;
	},
})
export default class FilterBarProvider extends Vue {
	_providersById: Record<number, Provider>;
	isModalOpening = false;
	providerUrls = {} as Record<string, string>;

	@Prop({ default: true }) addButtonVisible: boolean;
	@Prop({ default: false }) showAddButtonFirst: boolean;
	@Prop() collectionType: CollectionType;
	@Prop({ default: '' }) newPageType: NewPageType;
	@Prop() currentFilters: FilterCollection;
	@Prop({ type: Boolean, default: true }) providersClickable: boolean;
	@Prop({ default: () => [] }) userProviders: Provider[]; // used for serverCacheKey
	@Prop() userSettings: UserSettings; // used for serverCacheKey
	@Prop() webLocale: WebLocale; // used for serverCacheKey
	@Prop({ default: true }) updateRouteState: boolean; // when toggling providers
	@Prop({ default: true }) trackEvent: boolean; // when toggling providers
	@Prop() userSelectedPackages: string[]; // used for sports provider until we have a better flagging mechanism in place

	@Constant.Getter providers: () => Provider[];
	@Constant.Getter providersById: Record<number, Provider>;
	@Constant.Getter providersByShortName: Record<string, Provider>;
	@Constant.Getter genresByShortName: Record<string, Genre>;

	@Filter.Action toggle: (payload: {
		filterName: keyof FilterCollection;
		value: any;
		selectedState: 1 | 0;
		updateRouteState?: boolean;
		userSelectedPackages?: string[];
		trackEvent?: boolean;
	}) => void;

	@Routing.State activeRoute: any;

	@User.State settings: UserSettings;

	get IMG_PROVIDERS_BAR_LOAD_COUNT() {
		return global.IMG_PROVIDERS_BAR_LOAD_COUNT;
	}

	get plusIcon() {
		return `/${ASSETS_DIR}/img/plus_icon.png`;
	}

	get plusIconIsVisible() {
		// causing issue with SSR > client hydration

		if (this.addButtonVisible) {
			return this.settings?.providers?.length > 0;
		}
		return this.addButtonVisible;
	}

	get filteredProviders() {
		if (!this.activeRoute.name.startsWith('app.sports')) {
			let titlesOnlyProviders = [];
			titlesOnlyProviders = this.userProviders.filter((provider: Provider) => {
				return provider?.data?.has_titles || provider?.data?.has_titles === null;
			});
			return titlesOnlyProviders;
		}
		return this.userProviders;
	}

	get recalculatedProviderUrlProp() {
		const filtersProviders = this.currentFilters.providers.join('-');
		const filtersContentTypes = this.currentFilters.content_types.join('-');
		return [
			this.webLocale,
			filtersProviders,
			filtersContentTypes,
			this.isPricedropsPage ? 'pricedrop' : 'normal',
		].join('_');
	}

	get isPricedropsPage() {
		return this.collectionType === CollectionType.NEW && this.$route.meta?.newPageType === NewPageType.PriceDrop;
	}

	// provider links should't exist if it is a pricedrops url, because they only exist as query params, e.g. /us/on-sale?providers=itu
	get providerTag() {
		return this.isPricedropsPage ? 'button' : 'a';
	}

	// SSR perf: only call this.providersById once instead of every v-for loop.
	@Watch('userProviders', { immediate: true })
	onProvidersChange() {
		this._providersById = this.providersById;
	}

	@Watch('recalculatedProviderUrlProp', { immediate: true })
	async onRecalculatedProviderUrlPropChange() {
		// every call to a vuex getter seems to cost performance, that's why we only call it once before the loop
		const providersByShortName = this.providersByShortName;
		const genresByShortName = this.genresByShortName;
		// cached getters don't work in SSR, that's why we optimise it by putting it into data

		if (this.isPricedropsPage) {
			this.providerUrls = {};
		} else {
			this.providerUrls = this.userProviders.reduce((root, provider: Provider) => {
				root[provider.short_name] = calculateUrl(
					this.$router,
					undefined,
					this.collectionType,
					{
						providers: [provider.short_name],
						content_types: this.currentFilters.content_types,
					},
					providersByShortName,
					genresByShortName
				);
				return root;
			}, {} as Record<string, string>);
		}
	}

	providerClicked(providerName: string) {
		if (this.providersClickable) {
			const isActive: 1 | 0 = this.isFiltersContainsProvider(providerName) ? 0 : 1;
			const toggleData = {
				filterName: 'providers',
				value: providerName,
				selectedState: isActive,
				updateRouteState: this.updateRouteState,
				userSelectedPackages: this.userSelectedPackages,
				trackEvent: this.trackEvent,
			};
			providerPageExpTrackEvent({ action: 'click', label: 'filter_toggled_providers' });

			this.toggle(toggleData as any);
			this.$emit('toggle', toggleData);
		}
	}
	isFiltersContainsProvider(providerName: string): boolean {
		return this.currentFilters.providers.includes(providerName);
	}
	isProviderActive(providerName: string): boolean {
		return this.currentFilters.providers.length === 0 || this.isFiltersContainsProvider(providerName);
	}

	async openProviderSelection() {
		this.isModalOpening = true;

		await ModalHelper.openModal(
			PackagesSelectionModal,
			{ providerPlace: ProviderFilterPlace.SETTINGS },
			{ cssClass: 'packages-selection-modal' },
			ProviderFilterPlace.SETTINGS,
			true
		);
		this.isModalOpening = false;
	}
}
